import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { isNative, isIOS, isAndroid } from '../utils/platform';
import { getCachedIdToken } from '../../services/network';
import { stashPassCode } from '../../features/referral/referral';

/**
 * Native (Capacitor) app setup. Every plugin import is dynamic and wrapped in
 * try/catch so web builds are silent no-ops.
 *
 * Deep links (custom URL scheme `babytrack`, v1.5.0): `babytrack://pass/<code>`
 * opens the app on a friend's Premium pass. The code is stashed — warm opens
 * through `appUrlOpen`, cold starts through `App.getLaunchUrl()` as well — and
 * redeemed once signed in (usePendingPass, or the onboarding set-up step).
 * This runs outside the router, so it never navigates.
 *
 * `getLaunchUrl()` is not one-shot: it answers the URL this native process opened
 * LAST (Capacitor's ApplicationDelegateProxy.lastURL, never cleared), and the JS
 * starts again on every reload of the web view — a WebContent process crash, the
 * error screen's Reload, the 401 → /login reload. So the pass link handled is
 * remembered in sessionStorage, which lives exactly as long as that native URL (it
 * survives a reload of the web view, and is empty in a new app process), and a
 * getLaunchUrl() answer equal to it is ignored. `appUrlOpen` — a real tap — is always
 * handled, and also remembered, as it moves lastURL too.
 *
 * Status bar: Capacitor's `Style.Dark` means "LIGHT text, for a dark
 * background" and `Style.Light` means "dark text, for a light background" —
 * the naming describes the CONTENT, not the background. Getting this backwards
 * paints white glyphs on the white header. `applyStatusBarStyle(isDark)` is
 * exported so App.js can re-apply it whenever the resolved theme flips
 * (system change, or the Appearance control in Settings).
 */

// Header colour per theme (Android tints the status bar itself).
const ANDROID_BAR_COLOR = { light: '#ffffff', dark: '#1a1d25' };

const PASS_LINK_RE = /^babytrack:\/\/pass\//i;
let lastPassCode = null; // appUrlOpen and getLaunchUrl can both report a cold-start link
export const HANDLED_LINK_KEY = 'bt_pass_link_handled';

function readHandledLink() {
  try {
    return window.sessionStorage.getItem(HANDLED_LINK_KEY);
  } catch {
    return null;
  }
}

function rememberHandledLink(url) {
  try {
    window.sessionStorage.setItem(HANDLED_LINK_KEY, url);
  } catch {
    /* storage refused: a reload may stash the link again (the server answers a replay idempotently) */
  }
}

/**
 * Stash the code from a `babytrack://pass/<code>` link. Returns true when the
 * URL was a pass link with a valid code. Exported for tests.
 */
export function handlePassLink(url) {
  if (typeof url !== 'string' || !PASS_LINK_RE.test(url)) return false;
  const code = stashPassCode(url);
  if (!code) return false;
  if (code !== lastPassCode && !getCachedIdToken()) {
    // Signed out: say where the pass went, as nothing else will until sign-in.
    toast.success('Pass code saved. It will be added when you sign in.');
  }
  lastPassCode = code;
  return true;
}

export async function applyStatusBarStyle(isDark) {
  if (!isNative()) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light });
    if (isAndroid()) {
      await StatusBar.setBackgroundColor({
        color: isDark ? ANDROID_BAR_COLOR.dark : ANDROID_BAR_COLOR.light,
      });
    }
  } catch (e) {
    // Status bar plugin not available
  }
}

export default function useNativeSetup() {
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    if (!isNative()) return;

    async function setup() {
      // The initial style is applied from App.js as soon as the theme resolves;
      // this only covers the window before that effect runs.
      if (isIOS() || isAndroid()) {
        await applyStatusBarStyle(false);
      }

      try {
        const { SplashScreen } = await import('@capacitor/splash-screen');
        await SplashScreen.hide();
      } catch (e) {
        // Splash screen plugin not available
      }

      if (isAndroid()) {
        try {
          const { App } = await import('@capacitor/app');
          App.addListener('backButton', ({ canGoBack }) => {
            if (canGoBack) {
              window.history.back();
            } else {
              App.exitApp();
            }
          });
        } catch (e) {
          // App plugin not available
        }
      }

      // Handle OAuth callback from in-app browser (SFSafariViewController).
      // When Cognito redirects to capacitor://localhost/auth/callback,
      // close the in-app browser and let Amplify process the auth code.
      try {
        const { App } = await import('@capacitor/app');
        App.addListener('appUrlOpen', async ({ url }) => {
          if (url && url.includes('/auth/callback')) {
            try {
              const { Browser } = await import('@capacitor/browser');
              await Browser.close();
            } catch (e) {
              // Browser plugin not available
            }
          } else if (handlePassLink(url)) {
            rememberHandledLink(url);
          }
        });

        // Cold start from a pass link: appUrlOpen may have fired before this
        // listener existed, so read the launch URL too (stashing is idempotent) —
        // unless this web view session has handled that link already.
        try {
          const launch = await App.getLaunchUrl();
          const url = launch?.url;
          if (url && url !== readHandledLink() && handlePassLink(url)) rememberHandledLink(url);
        } catch (e) {
          // getLaunchUrl unavailable — appUrlOpen still covers warm opens
        }
      } catch (e) {
        // App plugin not available
      }
    }

    setup();
  }, []);
}
